import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Container, Row, Col, Card, CardBody, Collapse } from 'reactstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { Skeleton } from 'primereact/skeleton'

import BizinBackend from '../../helpers/bizin_helper'
import classnames from 'classnames'

// Importações de gráficos
import TrendChart from '../BI_WS0000/Graficos/TrendChart'
import TrendChartMulti from '../BI_WS0000/Graficos/TrendChartMulti'
import TrendDayChart from '../BI_WS0000/Graficos/TrendDayChart'
import BarChartY from '../BI_WS0000/Graficos/BarChartY'
import BarChartX from '../BI_WS0000/Graficos/BarChartX'
import BarChartXLine from '../BI_WS0000/Graficos/BarChartXLine'
import BarChartXMultiLine from '../BI_WS0000/Graficos/BarChartXMultiLine'
import ComposedCharts from '../BI_WS0000/Graficos/ComposedCharts'
import HeatMap from '../BI_WS0000/Graficos/HeatMap'
import MapChart from '../BI_WS0000/Graficos/MapChart'
import ScatterChart from '../BI_WS0000/Graficos/ScatterChart'
import LineChart from '../BI_WS0000/Graficos/LineChart'
import PieChart from '../BI_WS0000/Graficos/PieChart'
import { useSelector } from 'react-redux'

const BI_EX0000 = () => {
  const navigate = useNavigate()
  const [cookies, setCookie] = useCookies(['_csrf_token'])
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState({ grupos: [] })
  const [openAccordions, setOpenAccordions] = useState([])
  const [searchParams] = useSearchParams()
  const user = useSelector((state) => state.login.user)

  const params_principal = useMemo(() => {
    return {
      1: searchParams.get('1') || 'MYORDER_INI',
      2: searchParams.get('2') || '0',
      3: searchParams.get('3') || 'pt-BR',
      4: searchParams.get('4') || '3871',
      5: searchParams.get('5') || '',
      6: searchParams.get('6') || '20210101',
      7: searchParams.get('7') || '20211230'
    }
  }, [searchParams])

  const handleBack = () => navigate(-1)

  function corrigirJson(str) {
    if (!str || str.trim() === '') return null
    try {
      return JSON.parse(str)
    } catch (e) {
      const corrigido = str.replace(/(\w+):/g, '"$1":').replace(/'/g, '"')
      try {
        return JSON.parse(corrigido)
      } catch (err) {
        console.error('Falha ao parsear JSON corrigido:', corrigido, err)
        return null
      }
    }
  }

  function buildQuery(params) {
    const entries = Object.entries(params).map(([key, value]) => {
      const val = value == null ? '' : value
      return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
    })
    return entries.join('&')
  }

  const handleGoToOutraRota = () => {
    const queryString = buildQuery(params_principal)
    const url = `/relatorio?${queryString}`
    navigate(url)
  }

  const getCsrf = useCallback(async () => {
    try {
      const _csrf = await BizinBackend.getCsrfToken()
      if (_csrf?.csrfToken) {
        setCookie('_csrf_token', _csrf, { path: '/' })
        return _csrf.csrfToken
      } else {
        throw new Error('Resposta inválida ao obter CSRF token')
      }
    } catch (error) {
      console.error('Erro ao obter CSRF token:', error)
      return null
    }
  }, [setCookie])

  useEffect(() => {
    const fetchTerms = async () => {
      setLoading(true)
      try {
        let token = cookies?._csrf_token?.csrfToken

        if (!token) {
          token = await getCsrf()
          if (!token) throw new Error('Falha ao obter CSRF token')
        }

        const response = await BizinBackend.executeProcedure(
          params_principal,
          'FRONT_Workspace_Obter_Componentes_Dashboard',
          token,
          user?.access_token
        )

        const grupos = response?.resultset_1 || []
        const componentesRaw = response?.resultset_2 || []

        const componentes = componentesRaw.map(comp => {
          let dados = null
          try {
            if (comp.Conteudo_Dados && comp.Conteudo_Dados.trim() !== '') {
              dados = corrigirJson(comp.Conteudo_Dados)
            }
          } catch (err) {
            console.error(
              'Erro ao parse Conteudo_Dados para o componente:',
              comp.Descricao_Dashboard_Componente,
              err
            )
          }

          let opcoes = null
          try {
            if (comp.Conteudo_Opcoes && comp.Conteudo_Opcoes.trim() !== '') {
              opcoes = corrigirJson(comp.Conteudo_Opcoes)
            }
          } catch (err) {
            console.error(
              'Erro ao parse Conteudo_Opcoes para o componente:',
              comp.Descricao_Dashboard_Componente,
              err
            )
          }

          let meta = null
          try {
            if (comp.Valor_Meta && comp.Valor_Meta.trim() !== '') {
              meta = corrigirJson(comp.Valor_Meta)
            }
          } catch (err) {
            console.error(
              'Erro ao parse Valor_Meta para o componente:',
              comp.Descricao_Dashboard_Componente,
              err
            )
          }

          return {
            ...comp,
            dadosComponente: dados,
            opcoesComponente: opcoes,
            Valor_Meta: meta
          }
        })

        const gruposComComponentes = grupos.map(grupo => {
          let opcoesLayout = {}

          if (
            grupo.Template_Conteudo_Opcoes &&
            grupo.Template_Conteudo_Opcoes.trim() !== ''
          ) {
            try {
              opcoesLayout = corrigirJson(grupo.Template_Conteudo_Opcoes)
            } catch (err) {
              console.error(
                'Erro ao parse Template_Conteudo_Opcoes para o grupo:',
                grupo.ID_Dashboard_Grupo,
                err
              )
            }
          }

          return {
            ...grupo,
            Quantidade_Colunas_Grupo:
              opcoesLayout.Quantidade_Colunas_Grupo ||
              grupo.Quantidade_Colunas_Grupo,
            Quantidade_Linhas_Grupo:
              opcoesLayout.Quantidade_Linhas_Grupo ||
              grupo.Quantidade_Linhas_Grupo,
            Altura_Linhas: opcoesLayout.Altura_Linhas || grupo.Altura_Linhas,
            GridAutoRows: opcoesLayout.GridAutoRows || grupo.GridAutoRows,
            componentes: componentes.filter(
              comp => comp.ID_Dashboard_Grupo === grupo.ID_Dashboard_Grupo
            )
          }
        })

        setDashboardData({ grupos: gruposComComponentes })
        setOpenAccordions(
          gruposComComponentes.map(grupo => grupo.ID_Dashboard_Grupo)
        )

        setDashboardData({ grupos: gruposComComponentes })
        setOpenAccordions(
          gruposComComponentes.map(grupo => grupo.ID_Dashboard_Grupo)
        )
      } catch (err) {
        console.error('Erro ao carregar os dashboard:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchTerms()
  }, [cookies, params_principal])

  const toggleAccordion = groupId => {
    setOpenAccordions(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }

  const GridBizinAccordion = ({
    componentes,
    qtde,
    qtlLinha,
    alturaLinha,
    GridAutoRows
  }) => {
    if (!componentes || !Array.isArray(componentes)) return null

    const containerStyle = {
      display: 'grid',
      gridTemplateColumns: `repeat(${qtde}, 1fr)`,
      ...(GridAutoRows === 'auto'
        ? { gridAutoRows: 'auto' }
        : { gridTemplateRows: `repeat(${qtlLinha}, ${alturaLinha}px)` }),
      gap: '8px',
      width: '100%'
    }

    return (
      <div style={containerStyle}>
        {componentes.map(item => {
          const spanCols = item.Quantidade_Colunas_Componente || 1
          let valorIndicador = '-'
          if (item.dadosComponente) {
            if (
              'success' in item.dadosComponente &&
              item.dadosComponente.success &&
              item.dadosComponente.data &&
              item.dadosComponente.data.length > 0
            ) {
              valorIndicador = item.dadosComponente.data[0].Valor_Indicador
            } else if (
              Array.isArray(item.dadosComponente) &&
              item.dadosComponente.length > 0
            ) {
              valorIndicador = item.dadosComponente[0].Valor_Indicador
            } else if ('valor' in item.dadosComponente) {
              valorIndicador = item.dadosComponente.valor
            }
          }

          return (
            <div
              key={item.ID_Dashboard_Componente}
              style={{
                gridColumn: `span ${spanCols}`,
                border: '1px solid #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '100px'
              }}
            >
              {item.Chave_Especificacao_Componente === 'TrendChart' &&
              item.dadosComponente !== null ? (
                <TrendChart item={item} />
              ) : item.Chave_Especificacao_Componente === 'TrendChartMulti' &&
                item.dadosComponente !== null ? (
                <TrendChartMulti item={item} />
              ) : item.Chave_Especificacao_Componente === 'TrendDayChart' &&
                item.dadosComponente !== null ? (
                <TrendDayChart item={item} />
              ) : item.Chave_Especificacao_Componente === 'BarChartXLine' &&
                item.dadosComponente !== null ? (
                <BarChartXLine item={item} />
              ) : item.Chave_Especificacao_Componente ===
                  'BarChartXMultiLine' && item.dadosComponente !== null ? (
                <BarChartXMultiLine item={item} />
              ) : item.Chave_Especificacao_Componente === 'BarChartY' &&
                item.dadosComponente !== null ? (
                <BarChartY item={item} />
              ) : item.Chave_Especificacao_Componente === 'BarChartX' &&
                item.dadosComponente !== null ? (
                <BarChartX item={item} />
              ) : item.Chave_Especificacao_Componente === 'ComposedCharts' &&
                item.dadosComponente !== null ? (
                <ComposedCharts item={item} />
              ) : item.Chave_Especificacao_Componente === 'HeatMap' &&
                item.dadosComponente !== null ? (
                <HeatMap item={item} />
              ) : item.Chave_Especificacao_Componente === 'MapChart' &&
                item.dadosComponente !== null ? (
                <MapChart item={item} />
              ) : item.Chave_Especificacao_Componente === 'ScatterChart' &&
                item.dadosComponente !== null ? (
                <ScatterChart item={item} />
              ) : item.Chave_Especificacao_Componente === 'LineChart' &&
                item.dadosComponente !== null ? (
                <LineChart item={item} />
              ) : item.Chave_Especificacao_Componente === 'PieChart' &&
                item.dadosComponente !== null ? (
                <PieChart item={item} />
              ) : (
                <Card className="w-100 h-100">
                  <CardBody className="w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                    <div>{item.Sigla}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {valorIndicador}
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Skeleton width="100%" height="150px" />
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content">
      <Container fluid={true} className="mt-4">
        <Row>
          <Col lg={12}>
            <div className="mt-auto d-flex align-items-end justify-content-end">
              <div className="d-flex align-items-center gap-2 primary">
                <button
                  type="button"
                  className="btn btn-outline-light waves-effect"
                >
                  <i
                    className="fas fa-print font-size-16 primary"
                    style={{ cursor: 'pointer' }}
                  ></i>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-light waves-effect"
                >
                  <i
                    className="fas fa-save font-size-16"
                    style={{ cursor: 'pointer' }}
                  ></i>
                </button>
                <button
                  type="button"
                  className="btn btn-primary waves-effect waves-light"
                >
                  Filtro
                </button>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            {dashboardData.grupos.map((grupo, index) => {
              const groupId = grupo.ID_Dashboard_Grupo || index
              return (
                <div
                  key={groupId}
                  className="accordion-item accordion-item-dash"
                >
                  <h2
                    className="accordion-header accordion-header-dash"
                    id={`heading${groupId}`}
                  >
                    <button
                      type="button"
                      className={classnames(
                        'accordion-button accordion-button-dash',
                        'fw-medium',
                        { collapsed: !openAccordions.includes(groupId) }
                      )}
                      onClick={() => toggleAccordion(groupId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex w-100 align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <i
                            className={`font-size-24 text-color-azul1 font-w-400 p-0 m-0 fas ${
                              openAccordions.includes(groupId)
                                ? 'fa-angle-up seta-acordion'
                                : 'fa-angle-down seta-acordion'
                            }`}
                          ></i>
                          <span className="ms-2">
                            {grupo.Descricao_Dashboard_Grupo}
                          </span>
                        </div>
                      </div>
                    </button>
                  </h2>
                  <Collapse isOpen={openAccordions.includes(groupId)}>
                    <div className="accordion-body accordion-body-dash">
                      <GridBizinAccordion
                        componentes={grupo.componentes}
                        qtde={grupo.Quantidade_Colunas_Grupo}
                        qtlLinha={grupo.Quantidade_Linhas_Grupo}
                        alturaLinha={grupo.Altura_Linhas}
                        GridAutoRows={grupo.GridAutoRows}
                      />
                    </div>
                  </Collapse>
                </div>
              )
            })}
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default BI_EX0000
